import { useEffect, useRef, useCallback } from 'react';

interface UseAutoRefreshOptions {
  /** Interval in milliseconds between refreshes */
  interval?: number;
  /** Whether auto-refresh should be enabled */
  enabled?: boolean;
}

/**
 * Custom hook for automatic periodic refresh of data
 * @param refreshFn Function to call for refreshing data
 * @param options Configuration options for auto-refresh
 * @returns Object with manual refresh function and status
 */
export function useAutoRefresh(
  refreshFn: () => void,
  { interval = 30000, enabled = true }: UseAutoRefreshOptions = {}
) {
  // Reference to the interval timer
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Clear the existing interval
  const clearRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  // Set up the interval for auto-refresh
  const setupRefreshInterval = useCallback(() => {
    clearRefreshInterval();
    
    if (enabled) {
      refreshIntervalRef.current = setInterval(() => {
        refreshFn();
      }, interval);
    }
  }, [refreshFn, interval, enabled, clearRefreshInterval]);
  
  // Manually trigger a refresh
  const refresh = useCallback(() => {
    refreshFn();
  }, [refreshFn]);
  
  // Set up auto-refresh when component mounts or dependencies change
  useEffect(() => {
    setupRefreshInterval();
    
    // Cleanup on unmount
    return clearRefreshInterval;
  }, [setupRefreshInterval, clearRefreshInterval]);
  
  return {
    refresh,
    isEnabled: enabled,
  };
}